import { useTranslations } from '@aikoa/i18n/react'
import { Image as RNImage, type ImageErrorEventData, type ImageProps } from 'expo-image'
import { memo, useMemo, useState } from 'react'
import { View, type ImageStyle, type StyleProp } from 'react-native'
import { cn } from '~/lib/utils.ts'
import Icon from '../Icon'
import { Button } from '../ui/button'
import { Text } from '../ui/text'

const Image = memo(function Image({
  source,
  className,
  errorClassName,
  onPress,
  buttonLabel,
  ...imageProps
}: {
  className?: string
  errorClassName?: string
  onPress?: () => void
  buttonLabel?: string
} & ImageProps) {
  const { t } = useTranslations('common')
  const [imageError, setImageError] = useState(false)

  const imageStyle = useMemo(
    () =>
      [
        {
          width: '100%',
        },
        imageProps.style,
      ] satisfies StyleProp<ImageStyle>,
    [imageProps.style]
  )

  const {
    contentFit: _contentFit,
    cachePolicy: _cachePolicy,
    transition: _transition,
    onError: _onError,
    ...restImageProps
  } = imageProps

  return !imageError ? (
    <RNImage
      source={source}
      style={imageStyle}
      focusable={false}
      {...restImageProps}
      contentFit={_contentFit ?? ('cover' as const)}
      cachePolicy={_cachePolicy ?? 'memory-disk'}
      transition={_transition ?? 0}
      onError={(event: ImageErrorEventData) => {
        setImageError(true)
        _onError?.(event)
      }}
      className={className}
    />
  ) : (
    <View className={cn('w-full px-4 pt-4', errorClassName)} style={imageStyle}>
      <View className="flex-1 items-center justify-center gap-2 rounded-md border border-muted bg-muted/50 p-4 pb-2">
        <Icon name="cloud-offline" size={24} className="text-foreground" />
        <Text className="text-center text-sm text-foreground">
          {t(($) => $.fileUpload.loadFailed)}
        </Text>
        {buttonLabel && onPress && (
          <Button
            variant="link"
            size="sm"
            className="pb-0 text-xs text-foreground underline"
            onPress={onPress}>
            {buttonLabel}
          </Button>
        )}
      </View>
    </View>
  )
})

export default Image
